import { Input } from '@builder.io/sdk';

import { buttonFields } from 'molecules/Button/config';
import sectionFields from 'molecules/Section/config';

import { DEFAULT_SECTIONS, QUIZ_QUESTIONS } from 'components/BuildVsBuyQuiz/config/questions';

import { idField } from 'utils/builder/fields';

export * from './types';
export * from './questions';

/**
 * BuildVsBuyQuiz Component Configuration for Builder.io
 *
 * FULLY DYNAMIC QUIZ SYSTEM:
 * - Works with ANY number of questions (5, 13, 20, 100+)
 * - Automatic scoring adjustment based on question count
 * - Custom sections managed in separate list
 * - Two email capture variants (before/after quiz)
 * - Result-specific buttons configurable per result bucket
 * - Rich text descriptions for all results (supports formatting, line breaks, etc.)
 *
 * SCORING LOGIC (Automatic):
 * - "Clear" result: ~75-80% answers in one direction
 * - "Leans" result: ~60% answers in one direction
 * - "Toss Up": Everything else (balanced 40-60%)
 */
const buildVsBuyQuizFields = [
  {
    name: 'variant',
    type: 'string',
    enum: ['gated_start', 'gated_end'],
    defaultValue: 'gated_start',
    helperText:
      'Email capture timing (fully configurable): gated_start shows email form BEFORE quiz on landing page (Form 637), gated_end shows email form AFTER quiz on results page (Form 640). Choose gated_start for more leads or gated_end for higher engagement.',
    required: true,
  },

  {
    name: 'quizVersion',
    type: 'string',
    defaultValue: 'v1',
    helperText:
      'Version identifier for A/B testing and analytics tracking (fully configurable). Change this when testing different question sets (v1, v2, v3). Tracked in Segment and Google Analytics.',
  },

  {
    name: 'redirectUrl',
    type: 'string',
    defaultValue: '/results-build-vs-buy-quiz',
    helperText:
      'Results page URL where users are redirected after completing quiz (fully configurable). All quiz data is automatically passed as URL parameters including variant, result bucket, scores, and individual answers.',
    required: true,
  },

  {
    name: 'sections',
    type: 'list',
    helperText:
      'Section definitions with IDs and display names (fully configurable and dynamic). Define all quiz sections here - questions will reference these section IDs. One centralized place to manage section names ensures consistency across all questions. Add, remove, or rename sections and all questions update automatically. Default sections include business, resources, and technical, but you can create any custom sections you need.',
    defaultValue: DEFAULT_SECTIONS,
    subFields: [
      {
        name: 'id',
        type: 'string',
        required: true,
        helperText:
          'Unique section identifier used to group questions together (fully configurable). Use lowercase letters, hyphens, or underscores. Keep it short and memorable. Examples: business, resources, technical, budget, train, timeline. This ID is referenced by questions to determine their section grouping.',
      },
      {
        name: 'displayName',
        type: 'string',
        required: true,
        helperText:
          'Display name shown to users in the progress bar (fully configurable). Can use any characters including spaces, special characters, and unicode. Examples: "Business & customer impact", "Italia y Suiza", "Financial Considerations", "Project Timeline". Change this name here and all questions in this section update automatically.',
      },
    ],
  },

  {
    name: 'questions',
    type: 'list',
    helperText:
      'Quiz questions array with automatic dynamic scoring (fully configurable). Works with any number of questions - 5, 10, 13, 20, or more. Scoring thresholds automatically adjust based on total question count. Questions reference section IDs from the sections list above. Current default is 13 questions across 3 sections (business with 4 questions, resources with 3 questions, technical with 6 questions). Add or remove questions freely and scoring adapts automatically.',
    defaultValue: QUIZ_QUESTIONS,
    subFields: [
      idField,
      {
        name: 'section',
        type: 'string',
        required: true,
        helperText:
          'Section ID that matches a section from the sections list above (fully configurable). Questions with the same section ID are grouped together in the progress bar. Display name comes from the sections list automatically. Available default sections: business, resources, technical, plus any custom sections you added. If the section ID does not exist in the sections list, add it there first.',
      },
      {
        name: 'question',
        type: 'longText',
        required: true,
        helperText:
          'Question text displayed to users (fully configurable). Write any question that helps determine build vs buy recommendation. Examples: "Are integrations a primary competitive differentiator?", "Do you need integrations live in three months or less?", "How many engineers do you have?"',
      },
      {
        name: 'blueLabel',
        type: 'string',
        required: true,
        helperText:
          'BUILD answer label for blue button (fully configurable). This answer direction counts toward a "BUILD" recommendation. Examples: "Yes", "More than 10", "We can handle it internally". Choose labels that indicate building makes sense.',
      },
      {
        name: 'orangeLabel',
        type: 'string',
        required: true,
        helperText:
          'BUY answer label for orange button (fully configurable). This answer direction counts toward a "BUY" recommendation. Examples: "No", "Less than 10", "We need external help". Choose labels that indicate buying makes sense.',
      },
    ],
  },

  // Result content: Clear Buy (~75-80% orange)
  {
    name: 'clearBuyHeading',
    type: 'string',
    defaultValue: 'You should buy an integration platform',
    helperText:
      'Heading for clear buy result when user strongly favors buying (fully configurable). Shown when approximately 75-80% of answers are orange/BUY direction. Examples by question count: 5 questions = 4+ orange (80%), 13 questions = 10+ orange (77%), 20 questions = 15+ orange (75%). This indicates a strong recommendation to BUY.',
  },
  {
    name: 'clearBuyDescription',
    type: 'richText',
    defaultValue:
      '<p>Requirements, timelines, or resources make buying the clear choice. Building might delay time-to-market and slow core product development.</p>',
    helperText:
      'Description text for clear buy result explaining why buying is recommended (fully configurable). Supports rich text formatting including line breaks, bold, italics, lists, and more for better content control.',
  },
  {
    name: 'clearBuyButtons',
    type: 'list',
    helperText:
      'Call-to-action buttons for clear buy result (fully configurable). These buttons appear after the result heading and description. For variant A (gated_start), buttons show immediately. For variant B (gated_end), buttons show after user submits email form. Common examples: "Book a Demo", "View Pricing", "Learn More".',

    subFields: buttonFields,
  },

  // Result content: Leans Buy (~60% orange)
  {
    name: 'leansBuyHeading',
    type: 'string',
    defaultValue: 'You should probably buy an integration platform',
    helperText:
      'Heading for leans buy result when user moderately favors buying (fully configurable). Shown when approximately 60% of answers are orange/BUY direction. Examples by question count: 5 questions = 3 orange (60%), 13 questions = 8-9 orange (62-69%), 20 questions = 12-14 orange (60-70%). This indicates a moderate recommendation to BUY.',
  },
  {
    name: 'leansBuyDescription',
    type: 'richText',
    defaultValue:
      '<p>You have some build capabilities, but most factors favor buying. Calculate the opportunity costs of diverting devs from your core product.</p>',
    helperText:
      'Description text for leans buy result explaining why buying is probably recommended (fully configurable). Supports rich text formatting including line breaks, bold, italics, lists, and more for better content control.',
  },
  {
    name: 'leansBuyButtons',
    type: 'list',
    helperText:
      'Call-to-action buttons for leans buy result (fully configurable). These buttons appear after the result heading and description. For variant A (gated_start), buttons show immediately. For variant B (gated_end), buttons show after user submits email form.',

    subFields: buttonFields,
  },

  // Result content: Toss Up (40-60% either)
  {
    name: 'tossUpHeading',
    type: 'string',
    defaultValue: 'Consider both options carefully',
    helperText:
      'Heading for toss up result when answers are balanced (fully configurable). Shown when results are split approximately 40-60% in either direction with no clear winner. This indicates the decision requires careful evaluation of specific circumstances.',
  },
  {
    name: 'tossUpDescription',
    type: 'richText',
    defaultValue:
      '<p>Your situation is balanced. Evaluate total cost of ownership, maintenance burden, and strategic priorities before deciding.</p>',
    helperText:
      'Description text for toss up result suggesting careful consideration (fully configurable). Supports rich text formatting including line breaks, bold, italics, lists, and more for better content control.',
  },
  {
    name: 'tossUpButtons',
    type: 'list',
    helperText:
      'Call-to-action buttons for toss up result (fully configurable). These buttons appear after the result heading and description. For variant A (gated_start), buttons show immediately. For variant B (gated_end), buttons show after user submits email form.',
    subFields: buttonFields,
  },

  // ========================================
  // RESULT CONTENT - Leans Build (~60% Blue)
  // ========================================
  {
    name: 'leansBuildHeading',
    type: 'string',
    defaultValue: 'You should probably build your integration platform',
    helperText:
      'Heading for leans build result when user moderately favors building (fully configurable). Shown when approximately 60% of answers are blue/BUILD direction. Examples by question count: 5 questions = 3 blue (60%), 13 questions = 8-9 blue (62-69%), 20 questions = 12-14 blue (60-70%). This indicates a moderate recommendation to BUILD.',
  },
  {
    name: 'leansBuildDescription',
    type: 'richText',
    defaultValue:
      "<p>You have the technical capabilities and specialized needs, but be sure you're not underestimating maintenance costs. Building an integration platform is typically a multi-year commitment requiring 3-5 dedicated engineers.</p>",
    helperText:
      'Description text for leans build result explaining why building is probably recommended (fully configurable). Supports rich text formatting including line breaks, bold, italics, lists, and more for better content control.',
  },
  {
    name: 'leansBuildButtons',
    type: 'list',
    helperText:
      'Call-to-action buttons for leans build result (fully configurable). These buttons appear after the result heading and description. For variant A (gated_start), buttons show immediately. For variant B (gated_end), buttons show after user submits email form.',

    subFields: buttonFields,
  },

  // ========================================
  // RESULT CONTENT - Clear Build (~75-80% Blue)
  // ========================================
  {
    name: 'clearBuildHeading',
    type: 'string',
    defaultValue: 'You should build your integration platform',
    helperText:
      'Heading for clear build result when user strongly favors building (fully configurable). Shown when approximately 75-80% of answers are blue/BUILD direction. Examples by question count: 5 questions = 4+ blue (80%), 13 questions = 10+ blue (77%), 20 questions = 15+ blue (75%). This indicates a strong recommendation to BUILD.',
  },
  {
    name: 'clearBuildDescription',
    type: 'richText',
    defaultValue:
      "<p>You have unique technical requirements, sufficient engineering resources, and integrations aren't mission-critical to closing deals. You should probably revisit this decision annually as your company scales.</p>",
    helperText:
      'Description text for clear build result explaining why building is recommended (fully configurable). Supports rich text formatting including line breaks, bold, italics, lists, and more for better content control.',
  },
  {
    name: 'clearBuildButtons',
    type: 'list',
    helperText:
      'Call-to-action buttons for clear build result (fully configurable). These buttons appear after the result heading and description. For variant A (gated_start), buttons show immediately. For variant B (gated_end), buttons show after user submits email form.',
    subFields: buttonFields,
  },

  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const buildVsBuyQuizConfig = {
  name: 'BuildVsBuyQuiz',
  inputs: buildVsBuyQuizFields,
  defaultStyles: {
    minHeight: '600px',
  },
};

export type BuildVsBuyQuizProps = GenerateBuilder<typeof buildVsBuyQuizFields>;

export default buildVsBuyQuizConfig;
